import type {
  FacilityFlowCoreModulesPlanningDtosPlanningEntriesPlanningEntryListDto,
  FacilityFlowCoreModulesPlanningDtosPlanningEntriesPlanningEntryTaskDto,
  FacilityFlowApiControllersPlanningPlanningWeeksControllerPlanEntryDto,
  FacilityFlowCoreModulesEmployeesDtosEmployeeListDto,
} from '../api/generated/models';
import type { PlanningWeekData, EmployeePlanning, DayPlanning, TaskBlock } from '../types/planning';
import type { AbsenceListDto } from '../types';
import { PlanEntryStatus, PlanEntryConflictFlags } from './planningConstants';
import { addIsoDays } from './dateUtils';

/**
 * Transformiert Backend Planning Entry DTOs in Planning UI-Format
 * 
 * UPDATE: PlanningEntryListDto enthält jetzt Tasks direkt!
 * Keine lazy-loading mehr nötig - alle Daten kommen in einem Request.
 */
export function transformApiEntriesToWeekData(
  entries: FacilityFlowCoreModulesPlanningDtosPlanningEntriesPlanningEntryListDto[],
  weekStart: string,
  year: number,
  week: number,
  allEmployees?: FacilityFlowCoreModulesEmployeesDtosEmployeeListDto[],
): PlanningWeekData {
  // 1. Gruppiere Entries nach EmployeeId
  const employeeMap = new Map<string, FacilityFlowCoreModulesPlanningDtosPlanningEntriesPlanningEntryListDto[]>();
  
  entries.forEach((entry) => {
    const empId = entry.employeeId || '';
    if (!employeeMap.has(empId)) {
      employeeMap.set(empId, []);
    }
    employeeMap.get(empId)!.push(entry);
  });

  // 2. Generiere 7 Tage (Mo-So) für die Woche
  const weekDates = generateWeekDates(weekStart);

  const buildDays = (employeeEntries: FacilityFlowCoreModulesPlanningDtosPlanningEntriesPlanningEntryListDto[]): DayPlanning[] => {
    return weekDates.map((date) => {
      const entry = employeeEntries.find((e) => e.date === date);
      
      if (!entry) {
        return {
          date,
          type: 'empty',
          targetHours: 0,
          plannedHours: 0,
          tasks: [],
        };
      }

      if (entry.entryType === 'Absence') {
        return {
          date,
          type: 'absence',
          planningEntryId: entry.planningEntryId,
          absenceType: entry.absenceTypeName || 'Abwesend',
          absenceTypeColor: entry.absenceTypeColor || undefined,
          targetHours: 0,
          plannedHours: 0,
          tasks: [],
        };
      }

      return {
        date,
        type: 'workday',
        planningEntryId: entry.planningEntryId,
        targetHours: entry.targetHours || 0,
        plannedHours: entry.plannedHours || 0,
        tasks: transformTasks(entry.tasks || []),
        plannedLicensePlate: entry.plannedLicensePlate ?? undefined,
      };
    });
  };

  const emptyDays = (): DayPlanning[] => weekDates.map((date) => ({
    date,
    type: 'empty',
    targetHours: 0,
    plannedHours: 0,
    tasks: [],
  }));

  // 3. Transformiere zu EmployeePlanning[]
  const employees: EmployeePlanning[] = [];

  const getRoleSortOrder = (role: string | null | undefined): number => {
    const r = (role || '').toLowerCase();
    if (r === 'mitarbeiter') return 0;
    if (r === 'verwaltung') return 1;
    if (r === 'superadmin') return 2;
    return 1; // other (e.g. custom) after Mitarbeiter, with Verwaltung
  };

  // Start with employees that have entries
  employeeMap.forEach((employeeEntries, employeeId) => {
    const firstEntry = employeeEntries[0];
    const empFromList = allEmployees?.find((e) => e.id === employeeId);
    employees.push({
      id: employeeId,
      name: firstEntry.employeeName || 'Unbekannt',
      avatar: undefined,
      avatarColor: empFromList?.avatarColor ?? null,
      role: empFromList?.role ?? undefined,
      // undefined when the employee list is not loaded — only a loaded list can
      // assert "has no work schedule model" (null).
      workScheduleModelId: empFromList ? (empFromList.workScheduleModelId ?? null) : undefined,
      days: buildDays(employeeEntries),
    });
  });

  // Add employees from allEmployees that don't have entries (only active ones)
  if (allEmployees) {
    const existingIds = new Set(employees.map((e) => e.id));
    for (const emp of allEmployees) {
      if (!emp.id || existingIds.has(emp.id)) continue;
      if (emp.status && emp.status !== 'Active') continue;
      employees.push({
        id: emp.id,
        name: emp.fullName || `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || 'Unbekannt',
        avatar: undefined,
        avatarColor: emp.avatarColor ?? null,
        role: emp.role ?? undefined,
        workScheduleModelId: emp.workScheduleModelId ?? null,
        days: emptyDays(),
      });
    }
  }

  // Sort: Mitarbeiter first, then Verwaltung, then others
  employees.sort((a, b) => {
    const orderA = getRoleSortOrder(a.role);
    const orderB = getRoleSortOrder(b.role);
    if (orderA !== orderB) return orderA - orderB;
    return (a.name || '').localeCompare(b.name || '');
  });

  return {
    weekStart,
    year,
    week,
    employees,
  };
}

/**
 * Merged PlanEntries (Status/ConflictFlags) als Overlay in bestehendes WeekData.
 * PlanEntries werden nach EmployeeId + Date gruppiert, dann wird der
 * "schlimmste" Status/Flag pro Tag übernommen.
 */
export function mergePlanEntriesOverlay(
  weekData: PlanningWeekData,
  planEntries: FacilityFlowApiControllersPlanningPlanningWeeksControllerPlanEntryDto[],
): PlanningWeekData {
  if (!planEntries.length) return weekData;

  // Index: employeeId-date → aggregated status/flags
  const entryMap = new Map<string, { status: number; conflictFlags: number }>();

  for (const pe of planEntries) {
    const key = `${pe.employeeId}-${pe.date}`;
    const existing = entryMap.get(key);

    if (!existing) {
      entryMap.set(key, {
        status: pe.status ?? PlanEntryStatus.Planned,
        conflictFlags: pe.conflictFlags ?? 0,
      });
    } else {
      // Merge: höherer Status = schlimmerer Zustand (Conflict > Planned)
      if ((pe.status ?? PlanEntryStatus.Planned) === PlanEntryStatus.Conflict) {
        existing.status = PlanEntryStatus.Conflict;
      }
      // OR-merge der ConflictFlags (bitfield)
      existing.conflictFlags |= pe.conflictFlags ?? 0;
    }
  }

  return {
    ...weekData,
    employees: weekData.employees.map((emp) => ({
      ...emp,
      days: emp.days.map((day) => {
        const overlay = entryMap.get(`${emp.id}-${day.date}`);
        if (!overlay) return day;

        return {
          ...day,
          planEntryStatus: overlay.status,
          planEntryConflictFlags: overlay.conflictFlags,
          hasConflict: overlay.status === PlanEntryStatus.Conflict,
        };
      }),
    })),
  };
}

/**
 * Merged genehmigte Abwesenheiten als Overlay in bestehendes WeekData.
 * Wenn ein Mitarbeiter eine genehmigte Abwesenheit hat UND gleichzeitig einen
 * Arbeitstag mit Tasks geplant ist, wird der Tag als Konflikt markiert.
 */
export function mergeAbsencesOverlay(
  weekData: PlanningWeekData,
  absences: AbsenceListDto[],
): PlanningWeekData {
  if (!absences.length) return weekData;

  // Only consider approved absences
  const approvedAbsences = absences.filter((a) => a.approvalStatus === 'Approved');
  if (!approvedAbsences.length) return weekData;

  // Index: employeeId → list of absence date ranges
  const absenceMap = new Map<string, AbsenceListDto[]>();
  for (const absence of approvedAbsences) {
    const empId = absence.employeeId;
    if (!absenceMap.has(empId)) {
      absenceMap.set(empId, []);
    }
    absenceMap.get(empId)!.push(absence);
  }

  return {
    ...weekData,
    employees: weekData.employees.map((emp) => {
      const empAbsences = absenceMap.get(emp.id);
      if (!empAbsences) return emp;

      return {
        ...emp,
        days: emp.days.map((day) => {
          // Find an approved absence that covers this date
          const matchingAbsence = empAbsences.find((a) => {
            const start = a.startDate;
            const end = a.endDate;
            return day.date >= start && day.date <= end;
          });

          if (!matchingAbsence) return day;

          // If the day is a workday with tasks → conflict!
          if (day.type === 'workday' && day.tasks.length > 0) {
            return {
              ...day,
              hasConflict: true,
              planEntryConflictFlags: (day.planEntryConflictFlags ?? 0) | PlanEntryConflictFlags.Absence,
              absenceConflict: {
                absenceTypeName: matchingAbsence.absenceTypeName || 'Abwesenheit',
                absenceTypeColor: matchingAbsence.absenceTypeColor || undefined,
                approvalStatus: 'Approved',
              },
            };
          }

          // If the day is empty or workday without tasks, show as absence
          if (day.type === 'empty' || (day.type === 'workday' && day.tasks.length === 0)) {
            return {
              ...day,
              type: 'absence' as const,
              absenceType: matchingAbsence.absenceTypeName || 'Abwesenheit',
              absenceTypeColor: matchingAbsence.absenceTypeColor || undefined,
              targetHours: 0,
              plannedHours: 0,
            };
          }

          return day;
        }),
      };
    }),
  };
}

/**
 * Summe der geplanten und Soll-Stunden über eine Woche pro Mitarbeiter.
 */
export function weekTotals(emp: EmployeePlanning): { planned: number; target: number; ratio: number } {
  let planned = 0;
  let target = 0;
  for (const day of emp.days) {
    planned += day.plannedHours || 0;
    if (day.type === 'workday') target += day.targetHours || 0;
  }
  return { planned, target, ratio: target > 0 ? planned / target : 0 };
}

/**
 * Generiert Array mit 7 Datumsstrings (Mo-So) für eine Woche
 */
function generateWeekDates(weekStart: string): string[] {
  return Array.from({ length: 7 }, (_, i) => addIsoDays(weekStart, i));
}

/**
 * Transformiert Backend Task DTOs in Frontend TaskBlock Format
 */
function transformTasks(
  tasks: FacilityFlowCoreModulesPlanningDtosPlanningEntriesPlanningEntryTaskDto[]
): TaskBlock[] {
  return tasks.map((task) => ({
    id: task.planningEntryTaskId || '',
    taskId: task.taskId || '',
    taskName: task.taskName || 'Unbenannte Aufgabe',
    objectId: task.objectId,
    objectName: task.objectName || '',
    objectShortName: task.objectShortName ?? undefined,
    serviceId: task.serviceId,
    serviceName: task.serviceName || '',
    startTime: task.startTime || '00:00',
    endTime: task.endTime || '00:00',
    durationHours: (task.durationMinutes || 0) / 60,
  }));
}
